/**
 * The billing grid's column geometry - one definition, shared by the header,
 * every row and the totals footer.
 *
 * Widths are fixed rather than fluid because the first two columns are FROZEN
 * (brief §16: "Room / Status ... should remain frozen/sticky while the billing
 * columns scroll horizontally"). A sticky cell has to know its own left offset,
 * and that offset is the sum of the widths before it - so if the three places
 * that render a row disagreed about a width by a pixel, the frozen columns
 * would drift apart as the operator scrolls.
 */

export interface BillingColumn {
  key: string
  label: string
  width: number
  align?: 'left' | 'right'
  /** Frozen against the left edge while the billing columns scroll away. */
  frozen?: boolean
}

/**
 * Exactly the design's column set, with two approved deviations:
 *
 *  · PIECES is new. The prototype hard-codes 20 pieces for Wash & Iron, but a
 *    weight cannot yield a piece count - 5.2 kg is 12 shirts or 30
 *    handkerchiefs - and FRD §7 prints "WI-1/20" from that number. Guessing it
 *    would print tags that lie about what is in the bag.
 *  · "DC Rate" is renamed "DC ITEMS". In the prototype that column's header
 *    says Rate but its cell contains an Edit link, which invites an operator to
 *    type a rate into a column that has none.
 */
export const BILLING_COLUMNS: BillingColumn[] = [
  { key: 'select', label: '', width: 40, frozen: true },
  { key: 'room', label: 'Room', width: 96, frozen: true },
  // Wide enough to carry the status chip AND the "✓ Tags printed" marker on
  // one line (brief §27). Stacking them would push the row past its fixed
  // height and make 200 rows disagree about how tall a row is.
  { key: 'status', label: 'Status', width: 196, frozen: true },
  { key: 'weight', label: 'W & I', width: 92 },
  { key: 'rate', label: 'W&I Rate', width: 88 },
  { key: 'pieces', label: 'Pieces', width: 78 },
  { key: 'dc', label: 'DC Items', width: 104 },
  { key: 'shoe', label: 'Shoe Care', width: 104 },
  { key: 'total', label: 'Total', width: 104, align: 'right' },
  { key: 'menu', label: '', width: 48, align: 'right' },
]

/** Left offset of each frozen column - the running sum of the widths before it. */
export const FROZEN_OFFSETS: Record<string, number> = (() => {
  const offsets: Record<string, number> = {}
  let cursor = 0
  for (const column of BILLING_COLUMNS) {
    if (!column.frozen) break
    offsets[column.key] = cursor
    cursor += column.width
  }
  return offsets
})()

/** Total width of the frozen block, i.e. where the scrolling columns begin. */
export const FROZEN_WIDTH = BILLING_COLUMNS.filter((column) => column.frozen).reduce(
  (sum, column) => sum + column.width,
  0,
)

/** Never shrink the billing columns to fit - scroll instead (brief §16). */
export const GRID_MIN_WIDTH = BILLING_COLUMNS.reduce((sum, column) => sum + column.width, 0)
